import { useContext, useMemo } from "react";
import { useCompanyStore, useDisclosureStore, useHelperStore } from "~/stores";
import {
    fetchCompanies as fetchCompaniesAPI,
    fetchCompanyFilters as fetchCompanyFiltersAPI
} from "~/services/api/metadata";
import { CompanyFilter } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import { Language, LanguageContext, Locale } from "~/providers/LanguageProvider";
import { normalizeCompanyData } from "~/utils/normalize.utils";

const useCompanyList = () => {
    // Store states
    const activeDisclosure = useDisclosureStore(state => state.disclosure);

    // Store methods
    const dispatch = useCompanyStore(state => state.dispatch);
    const dispatchDisclosure = useDisclosureStore(state => state.dispatch);
    const dispatchHelper = useHelperStore(state => state.dispatch);
    const getDisclosureIDs = useDisclosureStore(state => state.getDisclosureIDs);
    const getCompaniesByDisclosure = useCompanyStore(state => state.getCompaniesByDisclosure);
    const selectedCompanyIds = useCompanyStore(state => state.selectedCompanyIds);
    const isCompanySelected = useCompanyStore(state => state.isCompanySelected);

    const { language } = useContext(LanguageContext);
    const langLocale = language === Language.EN ? Locale.US : Locale.FR;

    /** Fetches all companies with data for the Full Documents tab selection */
    const fetchCompanies = async () => {
        let response = null;
        dispatchHelper({ type: SearchActions.SET_IS_LOADING, payload: true });

        const fetchByDisclosure = async (disclosure: number) => {
            let res = await fetchCompanyFiltersAPI(disclosure, langLocale);
            const resFilters = res.data;
            const companyIds = resFilters.CompanyID ?? [];

            const filters = companyIds.map(
                (company, index) =>
                    ({
                        id: index,
                        companyId: Number(company),
                        disclosureId: disclosure
                    } as CompanyFilter)
            );
            return filters;
        };

        try {
            // Fetch all companies and their data
            response = await fetchCompaniesAPI();
            const resCompanies = response.data.companies;
            const normalizedCompanies = resCompanies.map(item => normalizeCompanyData(item));
            dispatch({
                type: SearchActions.SET_COMPANY_ITEMS,
                payload: normalizedCompanies
            });

            // Fetch company filters for each disclosure
            const disclosureIDs = getDisclosureIDs();
            const filterPromises = disclosureIDs.map(disclosure => fetchByDisclosure(disclosure));
            const filtersArrays = await Promise.all(filterPromises);

            // Flatten the array of arrays
            const companyFilters = filtersArrays.flat();
            dispatch({
                type: SearchActions.SET_COMPANY_FILTERS,
                payload: companyFilters
            });
        } catch (err: any) {
            console.error(err.message);
        } finally {
            dispatchHelper({ type: SearchActions.SET_IS_LOADING, payload: false });
        }
    };

    /** Company list by active disclosure */
    const filteredCompanies = useMemo(() => {
        const disclosureId = activeDisclosure?.exploreId || -1;
        return getCompaniesByDisclosure(disclosureId) ?? [];
    }, [activeDisclosure]);

    /** Checks company selection from store if it's selected */
    const checkItemSelection = (companyId: number) => {
        return isCompanySelected(companyId);
    };

    /** Updates store company selection */
    const handleCompanySelection = (isSelected: boolean, companyId: number) => {
        const disclosureItemParams = {
            disclosureId: activeDisclosure?.id || -1,
            value: companyId,
            isSelected
        };

        dispatchDisclosure({
            type: SearchActions.SET_DISCLOSURE_CATEGORY,
            payload: disclosureItemParams
        });

        if (isSelected) {
            dispatch({ type: SearchActions.SET_SELECTED_COMPANY, payload: companyId });
        } else {
            dispatch({ type: SearchActions.REMOVE_SELECTED_COMPANY, payload: companyId });
        }
    };

    return {
        fetchCompanies,
        filteredCompanies,
        selectedCompanyIds,
        checkItemSelection,
        handleCompanySelection
    };
};

export default useCompanyList;
